import "unfetch/polyfill";
import {AnyRecord, ApiError, Method, RequestConfig} from "services/http/types";
import {API_URL, API_VERSION} from "services/http/env";
import createHash from "services/http/createHash";
import {getBrandId} from "../../brand";

const createQueryString = (params: AnyRecord | undefined) => {
  if (!params) return "";

  const query = new URLSearchParams();

  const append = (key: string, value: any) => {
    if (value === undefined || value === null) return;

    const stringValue =
      value instanceof Date ? value.toISOString() : String(value);

    query.append(key, stringValue);
  };

  for (const key in params) {
    const value = params[key];

    if (Array.isArray(value)) {
      for (const v of value) append(key, v);
    } else {
      append(key, value);
    }
  }

  return query.toString();
};

const createCompleteURL = (
  path: string,
  queryParams: AnyRecord | undefined,
) => {
  const queryString = createQueryString(queryParams);

  const url = [
    API_URL,
    API_URL.endsWith("/") ? "" : "/",
    path.trim().replace(/(^\/+)|(\/+$)/g, ""),
    queryString ? (path.includes("?") ? "&" : "?") : "",
    queryString,
  ];

  return url.join("");
};

const replacePathParams = (pathTemplate: string, replacements: AnyRecord) => {
  const pathKeys = new Set<string>();

  // Replace path params like :id in /users/:id
  const path = pathTemplate.replace(/:(\w+)/g, (_, key) => {
    if (!(key in replacements) || replacements[key] === undefined)
      throw Error(`Missing param key "${key}" for "${pathTemplate}"`);

    pathKeys.add(key);

    return replacements[key];
  });

  // Collect other args that are not path params
  const otherArgs: AnyRecord = {};
  for (const key of Object.keys(replacements))
    if (!pathKeys.has(key)) otherArgs[key] = replacements[key];

  // Return the final path and the other args
  return [path, otherArgs] as const;
};

const pendingRequests = new Map<string, Promise<unknown>>();

const BODY_METHODS = new Set<Method>(["POST", "PUT", "PATCH"]);

const createRequestHandler =
  <Res, Args extends AnyRecord | void>(config: RequestConfig) =>
  (params: Args) => {
    const hash = createHash({
      method: config.method,
      path: config.path,
      params,
    });
    const isMutation = config.method !== "GET";

    // Check for ongoing requests to prevent duplicate calls
    if (!isMutation) {
      const ongoingRequest = pendingRequests.get(hash);
      if (ongoingRequest) return ongoingRequest as Promise<Res>;
    }

    const [path, httpParams] = replacePathParams(config.path, params ?? {});
    const url = createCompleteURL(
      path,
      BODY_METHODS.has(config.method) ? undefined : httpParams,
    );

    const headers: Record<string, string> = {
      "accept-api-version": String(API_VERSION),
      "Content-Type": "application/json",
    };

    const brand = getBrandId();
    if (brand) headers["Accept-Brand"] = String(brand);

    const pendingRequest = fetch(url, {
      method: config.method,
      credentials: "include",
      cache: "no-cache",
      redirect: "follow",
      headers,
      body: BODY_METHODS.has(config.method)
        ? JSON.stringify(httpParams)
        : undefined,
    })
      .catch(() => {
        throw new ApiError(500);
      })
      .then(async res => {
        if (res.ok) {
          return (await res.json().catch(() => undefined)) as Promise<Res>;
        }

        // Too old api version
        // if (res.status === 418)
        //   reloadPage();

        // // Invalid access token
        // if (status === 401) {
        //   // Todo: Issue a refresh request
        //   forceReloadPage();
        // }

        const errorData = await res
          .json()
          .then(data => ("errors" in data ? data.errors : data))
          .catch(() => undefined);

        throw new ApiError(
          res.status,
          errorData?.message || res.statusText,
          errorData,
        );
      })
      .finally(() => {
        pendingRequests.delete(hash);
      });

    pendingRequests.set(hash, pendingRequest);
    return pendingRequest;
  };

export default createRequestHandler;
